// [S-708 / E-PERSONA-ADMIN] Persona configuration — detail/edit view.
//
// Structured editing, not a free-text prompt replacement: the locked default
// prompt (the persona manifest assembled at rest, unmerged) renders read-only; the only prompt
// field an admin can edit is `additionalInstructions`, which
// PersonaConfigService always APPENDS after the full default prompt — an
// admin can never silently strip GROUNDING / CONFIRM_BEFORE_REVIEW /
// ROUTE_TO_SPECIALIST, because the merge function structurally can't produce a
// prompt lacking them (packages/core/src/services/PersonaConfigService.ts).
//
// Tools, model, and limits are structured controls (checkboxes / a select /
// number inputs) sourced from the server's own catalogs (toolCatalog,
// supportedModels) — never free text, so an admin can't invent a tool or
// model id that doesn't exist.
//
// Draft → Publish lifecycle: edits save as a draft; Publish copies it to the
// live (published) override, which ChatService resolves on the very next
// chat turn. There is no live "test this draft" preview in this pass — that
// needs a new ephemeral chat-serving path apps/admin doesn't have yet
// (apps/chatbot owns the real ChatAgentPort/tool wiring); flagged as a
// follow-up story rather than shipped as a fake preview.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Checkbox, LoadingState, PageHead, Select } from "../../components";
import { getApiErrorMessage } from "../../data/api";
import type { ChatAgentKind } from "../../data/chatQuery";
import {
  EMPTY_PERSONA_OVERRIDE_FIELDS,
  usePersonaConfig,
  useDiscardPersonaDraft,
  usePublishPersonaConfig,
  useRevertPersonaConfig,
  useSavePersonaDraft,
  type PersonaOverrideFieldsWire,
} from "../../data/personaConfigQuery";
import { WorkflowStepView } from "./WorkflowStepView";

const AGENT_KINDS: ChatAgentKind[] = [
  "assistant",
  "drafting",
  "analysis",
  "negotiation",
  "longform",
];

function isChatAgentKind(v: string | undefined): v is ChatAgentKind {
  return v !== undefined && (AGENT_KINDS as string[]).includes(v);
}

export function PersonaDetail(): JSX.Element {
  const { kind: kindParam } = useParams<{ kind: string }>();
  const navigate = useNavigate();

  if (!isChatAgentKind(kindParam)) {
    return (
      <div className="page pa">
        <Alert label="Unknown persona" tone="error">
          "{kindParam}" isn't a recognized persona.
        </Alert>
      </div>
    );
  }
  return <PersonaDetailLoaded kind={kindParam} onBack={() => navigate("/admin/personas")} />;
}

function PersonaDetailLoaded({
  kind,
  onBack,
}: {
  kind: ChatAgentKind;
  onBack: () => void;
}): JSX.Element {
  const query = usePersonaConfig(kind);
  const saveDraft = useSavePersonaDraft();
  const discardDraft = useDiscardPersonaDraft();
  const publish = usePublishPersonaConfig();
  const revert = useRevertPersonaConfig();

  const [fields, setFields] = useState<PersonaOverrideFieldsWire>(
    EMPTY_PERSONA_OVERRIDE_FIELDS
  );

  // Reset the edit form from the server's current draft (falling back to the
  // published override, then pure defaults) whenever the persona changes or
  // reloads — never mid-edit, so an in-progress edit isn't clobbered by a
  // background refetch.
  useEffect(() => {
    if (query.data === undefined) return;
    const seed = query.data.override?.draft ?? query.data.override?.published ?? null;
    setFields(seed ?? EMPTY_PERSONA_OVERRIDE_FIELDS);
  }, [kind, query.data?.override?.draft, query.data?.override?.published]);

  if (query.isLoading) return <LoadingState label="Loading persona…" />;
  if (query.isError || query.data === undefined) {
    return (
      <Alert label="Couldn't load this persona" tone="error">
        {getApiErrorMessage(query.error) ?? "Unknown error"}
      </Alert>
    );
  }

  const { default: base, effective, override, toolCatalog, pinnedTools, supportedModels, workflowSteps } =
    query.data;

  const effectiveTools = fields.allowedToolsOverride ?? base.allowedTools;
  const toggleTool = (name: string, on: boolean): void => {
    const current = fields.allowedToolsOverride ?? [...base.allowedTools];
    const next = on ? [...new Set([...current, name])] : current.filter((t) => t !== name);
    setFields({ ...fields, allowedToolsOverride: next });
  };

  const busy =
    saveDraft.isPending || discardDraft.isPending || publish.isPending || revert.isPending;
  const mutationError =
    saveDraft.error ?? discardDraft.error ?? publish.error ?? revert.error ?? null;

  return (
    <div className="page pa">
      <div className="pa-detail__back">
        <Button variant="ghost" size="s" onClick={onBack}>
          ← All personas
        </Button>
      </div>
      <PageHead
        eyebrow="Agent Personas · Persona configuration"
        title={base.label}
        sub={base.blurb}
      />

      {mutationError !== null && (
        <Alert label="That didn't save" tone="error">
          {getApiErrorMessage(mutationError) ?? "Unknown error"}
        </Alert>
      )}

      <div className="pa-detail__section">
        <h2>Shared safety directives — locked</h2>
        <p className="pa-detail__hint">
          Every persona's system prompt starts here — GROUNDING, degraded-retrieval
          honesty, and (where applicable) the confirm-before-review and in-app routing
          directives. This can't be edited from this page; it's version-controlled in
          the persona's skill file and reviewed in commits.
        </p>
        <div className="pa-detail__locked-prompt">{base.systemPrompt}</div>
      </div>

      <div className="pa-detail__section">
        <h2>Additional instructions</h2>
        <p className="pa-detail__hint">
          Appended AFTER the locked prompt above — this can add behavior but can never
          remove or override the safety directives.
        </p>
        <textarea
          className="pa-detail__textarea"
          value={fields.additionalInstructions ?? ""}
          maxLength={4000}
          placeholder="e.g. Always mention the studio's standard force-majeure clause when discussing termination."
          onChange={(e) =>
            setFields({
              ...fields,
              additionalInstructions: e.target.value.length > 0 ? e.target.value : null,
            })
          }
        />
      </div>

      <div className="pa-detail__section">
        <h2>Tools</h2>
        <p className="pa-detail__hint">
          {pinnedTools.length > 0
            ? "Greyed-out tools are required by this persona's bound workflow and can't be removed."
            : "Which platform tools this persona may call."}
        </p>
        <div className="pa-detail__tools">
          {toolCatalog.map((name) => {
            const pinned = pinnedTools.includes(name);
            const checked = effectiveTools.includes(name);
            return (
              <div
                key={name}
                className={pinned ? "pa-detail__tool is-pinned" : "pa-detail__tool"}
              >
                <Checkbox
                  checked={checked}
                  disabled={pinned}
                  onCheckedChange={(on) => toggleTool(name, on)}
                  label={<code>{name}</code>}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="pa-detail__section">
        <h2>Model &amp; limits</h2>
        <div className="pa-detail__limits">
          <Select
            label="Model"
            value={fields.modelOverride ?? undefined}
            options={supportedModels.map((m) => ({ value: m, label: m }))}
            placeholder={`Default (${base.model ?? "adapter default"})`}
            onValueChange={(v) => setFields({ ...fields, modelOverride: v ?? null })}
          />
          <LimitField
            label="Max steps"
            value={fields.maxStepsOverride}
            fallback={base.maxSteps}
            onChange={(v) => setFields({ ...fields, maxStepsOverride: v })}
          />
          <LimitField
            label="Max tool calls"
            value={fields.maxToolCallsOverride}
            fallback={base.maxToolCalls}
            onChange={(v) => setFields({ ...fields, maxToolCallsOverride: v })}
          />
          <LimitField
            label="Max tokens"
            value={fields.maxTokensOverride}
            fallback={base.maxTokens}
            onChange={(v) => setFields({ ...fields, maxTokensOverride: v })}
          />
        </div>
      </div>

      {workflowSteps !== null && (
        <div className="pa-detail__section">
          <h2>Bound workflow</h2>
          <p className="pa-detail__hint">
            This persona follows a server-held state machine, not just its prompt — the
            steps below are fixed logic, not admin-editable.
          </p>
          <WorkflowStepView steps={workflowSteps} />
        </div>
      )}

      <div className="pa-detail__section">
        <h2>Save &amp; publish</h2>
        <p className="pa-detail__meta">
          {override?.draft != null
            ? "Draft pending — not yet live."
            : "No pending draft."}{" "}
          {override?.published != null
            ? `Published override live${override.publishedAt !== null ? ` since ${new Date(override.publishedAt).toLocaleString()}` : ""}.`
            : "Currently the pure code default."}
        </p>
        <div className="pa-detail__actions">
          <Button
            variant="primary"
            loading={saveDraft.isPending}
            onClick={() => saveDraft.mutate({ kind, fields })}
          >
            Save draft
          </Button>
          <Button
            variant="secondary"
            disabled={override?.draft == null}
            loading={publish.isPending}
            onClick={() => publish.mutate(kind)}
          >
            Publish
          </Button>
          <Button
            variant="ghost"
            disabled={override?.draft == null}
            loading={discardDraft.isPending}
            onClick={() => discardDraft.mutate(kind)}
          >
            Discard draft
          </Button>
          <Button
            variant="danger"
            disabled={override?.published == null}
            loading={revert.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Revert ${base.label} to its code default? The published override will be removed.`
                )
              ) {
                revert.mutate(kind);
              }
            }}
          >
            Revert to default
          </Button>
        </div>
      </div>

      {/* Reference only — not itself editable; shows what live chat resolves to today. */}
      <details className="pa-detail__section">
        <summary>Effective system prompt (live, for reference)</summary>
        <div className="pa-detail__locked-prompt">{effective.systemPrompt}</div>
      </details>

      {busy && <LoadingState label="Working…" />}
    </div>
  );
}

function LimitField({
  label,
  value,
  fallback,
  onChange,
}: {
  label: string;
  value: number | null;
  fallback: number;
  onChange: (v: number | null) => void;
}): JSX.Element {
  return (
    <div className="pa-detail__field">
      <label>{label}</label>
      <input
        type="number"
        value={value ?? ""}
        placeholder={`Default (${fallback})`}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw.trim().length === 0 ? null : Number(raw));
        }}
      />
    </div>
  );
}
